import { Loader2, Square } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { VoiceError, VoiceState } from "./use-voice-capture";

interface VoiceCaptureButtonProps {
  state: VoiceState;
  error: VoiceError | null;
  isSupported: boolean;
  /** Milliseconds elapsed since recording started. Only consulted when state === "recording". */
  elapsedMs: number;
  onStart: () => void;
  onStop: () => void;
  onDismissError: () => void;
  disabled?: boolean;
}

function errorMessage(err: VoiceError): string {
  switch (err) {
    case "permission_denied":
      return "Microphone access was denied. Allow mic access to use voice input.";
    case "unsupported":
      return "Voice input isn't supported on this device.";
    case "no_audio":
      return "No audio detected. Try again a bit closer to the mic.";
    case "recording_failed":
      return "Recording failed. Please try again.";
    case "request_failed":
      return "Couldn't process that audio. Please try again.";
    default:
      return "Voice input failed.";
  }
}

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function MicSparkleIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <rect x="9" y="2" width="6" height="13" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v4" />
      <path d="M8 22h8" />
      <path
        d="M19 3 L19.6 4.4 L21 5 L19.6 5.6 L19 7 L18.4 5.6 L17 5 L18.4 4.4 Z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  );
}

export function VoiceCaptureButton({
  state,
  error,
  isSupported,
  elapsedMs,
  onStart,
  onStop,
  onDismissError,
  disabled,
}: VoiceCaptureButtonProps) {
  if (!isSupported) return null;

  const isRecording = state === "recording";
  const isProcessing = state === "processing";

  const handleClick = () => {
    if (isRecording) {
      onStop();
      return;
    }
    if (error) onDismissError();
    onStart();
  };

  const label = isProcessing
    ? "Transcribing…"
    : isRecording
      ? `Listening · ${formatElapsed(elapsedMs)} · Tap to stop`
      : "Speak — AI fills the form";

  const ariaLabel = isProcessing
    ? "Transcribing your description"
    : isRecording
      ? `Stop recording, currently at ${formatElapsed(elapsedMs)}`
      : "Start voice fill";

  return (
    <div className="space-y-1.5">
      <Button
        type="button"
        variant={isRecording ? "destructive" : "default"}
        className={cn(
          "relative h-12 w-full gap-2.5 text-sm font-semibold",
          // Idle and done states use a dark CTA (foreground token) rather
          // than the otus brand `primary` cyan. The voice button is a
          // distinct "AI surface" — keeping it dark separates it from the
          // theme's other primary-cyan CTAs (Submit, etc.) and matches
          // the design Vinson signed off on.
          !isRecording &&
            !isProcessing &&
            "bg-foreground text-background hover:bg-foreground/90",
          isRecording && "animate-pulse",
        )}
        disabled={disabled || isProcessing}
        onClick={handleClick}
        aria-pressed={isRecording}
        aria-busy={isProcessing}
        aria-label={ariaLabel}
        data-voice-state={state}
      >
        {isProcessing ? (
          <Loader2 className="h-[18px] w-[18px] animate-spin" aria-hidden="true" />
        ) : isRecording ? (
          <Square
            className="h-[18px] w-[18px] fill-current"
            aria-hidden="true"
          />
        ) : (
          <MicSparkleIcon className="h-[18px] w-[18px]" />
        )}
        {label}
      </Button>
      {error && !isRecording && !isProcessing && (
        <p className="text-xs text-destructive">{errorMessage(error)}</p>
      )}
    </div>
  );
}
